import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export interface Banner {
  id: string;
  slot: string;
  title: string | null;
  subtitle: string | null;
  image_url: string | null;
  cta_label: string | null;
  cta_href: string | null;
  is_active: boolean;
  position: number;
}

interface Props {
  slot: string;
  className?: string;
  variant?: "hero" | "compact";
}

const SiteBanner = ({ slot, className = "", variant = "hero" }: Props) => {
  const BannerHeading = slot === "guide_hero" ? "h2" : "h1";
  const [banner, setBanner] = useState<Banner | null>(null);

  useEffect(() => {
    let alive = true;
    supabase
      .from("site_banners")
      .select("*")
      .eq("slot", slot)
      .eq("is_active", true)
      .order("position", { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (alive && data) setBanner(data as Banner);
      });
    return () => { alive = false; };
  }, [slot]);

  if (!banner) return null;

  const isHero = variant === "hero";

  return (
    <section
      className={`relative overflow-hidden ${isHero ? "rounded-none" : "rounded-2xl"} ${className}`}
      aria-label={banner.title ?? "Bannière"}
    >
      {banner.image_url && (
        <>
          <img
            src={banner.image_url}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover ${isHero ? "" : ""}`}
            loading={isHero ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/60 to-primary/30" />
        </>
      )}
      <div
        className={`relative z-10 ${
          isHero ? "py-20 md:py-28 lg:py-32" : "py-10 md:py-14"
        } px-6 md:px-10 max-w-5xl text-white`}
      >
        {banner.title && (
          <BannerHeading className={`font-heading font-extrabold leading-tight ${isHero ? "text-4xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
            {banner.title}
          </BannerHeading>
        )}
        {banner.subtitle && (
          <p className="mt-4 font-body text-base md:text-xl text-white/90 max-w-2xl leading-relaxed">
            {banner.subtitle}
          </p>
        )}
        {banner.cta_label && banner.cta_href && (
          <Button
            asChild
            size="lg"
            className="mt-7 bg-accent text-accent-foreground hover:bg-accent/90 font-body font-bold rounded-full px-7 shadow-xl"
          >
            <a href={banner.cta_href}>
              {banner.cta_label}
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        )}
      </div>
    </section>
  );
};

export default SiteBanner;